<template>
  <div class="hash-power-orb-wrapper" ref="containerRef">
    <canvas ref="canvasRef" class="orb-canvas"></canvas>

    <div class="orb-title">
      <h2 class="orb-title-text">{{ title }}</h2>
    </div>

    <div class="orbs-row">
      <div class="orb-item">
        <div
          ref="globalOrbRef"
          class="glass-orb orb-large orb-float"
          :style="orangeOrbStyle"
        >
          <div class="orb-highlight"></div>
          <div class="orbit-ring orbit-spin" :style="{ borderColor: 'rgba(255,69,0,0.2)' }">
            <div class="orbit-dot" :style="{ background: '#FF4500', boxShadow: '0 0 8px #FF4500' }"></div>
          </div>
          <div class="orb-content">
            <span class="orb-label">{{ displayGlobalLabel }}</span>
            <span class="orb-value" :style="{ background: 'linear-gradient(135deg, #FF4500, #FF8C00)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }">
              {{ globalHashPower }}
            </span>
            <span class="orb-unit" :style="{ color: '#FF4500' }">{{ globalUnit }}</span>
          </div>
          <div class="orb-glow orb-breathe" :style="{ background: 'radial-gradient(ellipse, rgba(255,69,0,0.06) 0%, transparent 70%)' }"></div>
        </div>
      </div>

      <div class="orb-item">
        <div
          ref="myOrbRef"
          class="glass-orb orb-medium orb-float-alt"
          :style="goldOrbStyle"
        >
          <div class="orb-highlight"></div>
          <div class="orbit-ring orbit-spin" :style="{ borderColor: 'rgba(255,215,0,0.2)' }">
            <div class="orbit-dot" :style="{ background: '#FFD700', boxShadow: '0 0 8px #FFD700' }"></div>
          </div>
          <div class="orb-content">
            <span class="orb-label">{{ displayMyLabel }}</span>
            <span class="orb-value" :style="{ background: 'linear-gradient(135deg, #FFD700, #C9A84C)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }">
              {{ myHashPower }}
            </span>
            <span class="orb-unit" :style="{ color: '#FFD700' }">{{ myUnit }}</span>
          </div>
          <div class="orb-glow orb-breathe-gold" :style="{ background: 'radial-gradient(ellipse, rgba(255,215,0,0.06) 0%, transparent 70%)' }"></div>
        </div>
      </div>
    </div>
    <div class="stats-row">
      <div class="stat-card" v-for="value in stats">
        <div class="stat-value" :style="{ color: value.color }">{{ value.value }}</div>
        <div class="stat-label">{{ value.label }}</div>
      </div>
    </div>

  </div>
</template>

<script>
export default {
  name: 'HashPowerOrb',
  props: {
    globalHashPower: { type: String, default: '0M' },
    globalUnit: { type: String, default: 'TH/s' },
    globalLabel: { type: String, default: '' },
    myHashPower: { type: String, default: '0K' },
    myUnit: { type: String, default: 'TH/s' },
    myLabel: { type: String, default: '' },
    title: { type: String, default: 'Hash Power Network' },
    showStats: { type: Boolean, default: true },
    stats: {
      type: Array,
      default: function () {
        return []
      },
    },
  },
  data() {
    return {
      globalPos: { x: 0, y: 0 },
      myPos: { x: 0, y: 0 },
      particles: [],
      animId: 0,
      frame: 0,
      cleanupAnimation: null,
      resizeObserver: null,
    }
  },
  computed: {
    displayGlobalLabel() {
      return this.globalLabel || this.$t('hashPower.globalLabel')
    },
    displayMyLabel() {
      return this.myLabel || this.$t('hashPower.myLabel')
    },
    computedStats() {
      if (this.stats && this.stats.length) return this.stats
      return [
        { label: this.$t('hashPower.stats.nodeCount'), value: '0', color: '#FF6B35' },
        { label: this.$t('hashPower.stats.todayYield'), value: '+0%', color: '#4CAF50' },
        { label: this.$t('hashPower.stats.totalStaked'), value: '0M', color: '#FFD700' },
      ]
    },
    orangeOrbStyle() {
      return {
        background:
          'radial-gradient(ellipse at 35% 35%, rgba(255,69,0,0.08) 0%, rgba(0,0,0,0.25) 50%, rgba(0,0,0,0.45) 100%)',
        filter: 'drop-shadow(0 0 20px rgba(255,69,0,0.4))',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        border: '1px solid rgba(255,69,0,0.25)',
        boxShadow:
          '0 0 30px rgba(255,69,0,0.4), 0 0 60px rgba(255,69,0,0.15), inset 0 1px 0 rgba(255,255,255,0.1)',
      }
    },
    goldOrbStyle() {
      return {
        background:
          'radial-gradient(ellipse at 35% 35%, rgba(255,215,0,0.08) 0%, rgba(0,0,0,0.25) 50%, rgba(0,0,0,0.45) 100%)',
        filter: 'drop-shadow(0 0 20px rgba(255,215,0,0.4))',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        border: '1px solid rgba(255,215,0,0.25)',
        boxShadow:
          '0 0 30px rgba(255,215,0,0.4), 0 0 60px rgba(255,215,0,0.15), inset 0 1px 0 rgba(255,255,255,0.1)',
      }
    },
  },
  mounted() {
    this.$nextTick(() => {
      setTimeout(() => {
        this.updatePositions()
        this.cleanupAnimation = this.startAnimation()
      }, 300)
      window.addEventListener('resize', this.updatePositions)
    })
  },
  beforeDestroy() {
    if (typeof this.cleanupAnimation === 'function') {
      this.cleanupAnimation()
    }
    window.removeEventListener('resize', this.updatePositions)
  },
  watch: {
    globalHashPower() {
      this.$nextTick(this.updatePositions)
    },
    myHashPower() {
      this.$nextTick(this.updatePositions)
    },
  },
  methods: {
    updatePositions() {
      var container = this.$refs.containerRef
      if (!container) return
      var containerRect = container.getBoundingClientRect()
      var gOrb = this.$refs.globalOrbRef
      var mOrb = this.$refs.myOrbRef
      if (gOrb) {
        var rectG = gOrb.getBoundingClientRect()
        this.globalPos = {
          x: rectG.left - containerRect.left + rectG.width / 2,
          y: rectG.top - containerRect.top + rectG.height / 2,
        }
      }
      if (mOrb) {
        var rectM = mOrb.getBoundingClientRect()
        this.myPos = {
          x: rectM.left - containerRect.left + rectM.width / 2,
          y: rectM.top - containerRect.top + rectM.height / 2,
        }
      }
    },
    spawnParticle(targetX, targetY, color) {
      var angle = Math.random() * Math.PI * 2
      var radius = 120 + Math.random() * 80
      return {
        x: targetX + Math.cos(angle) * radius,
        y: targetY + Math.sin(angle) * radius,
        vx: 0,
        vy: 0,
        size: Math.random() * 2 + 0.8,
        opacity: Math.random() * 0.4 + 0.2,
        color: color,
        targetX: targetX,
        targetY: targetY,
        life: 0,
        maxLife: 120 + Math.random() * 80,
      }
    },
    startAnimation() {
      var canvas = this.$refs.canvasRef
      if (!canvas) return
      var ctx = canvas.getContext('2d')
      if (!ctx) return
      var resize = () => {
        canvas.width = canvas.offsetWidth
        canvas.height = canvas.offsetHeight
      }
      resize()
      if (typeof ResizeObserver !== 'undefined') {
        this.resizeObserver = new ResizeObserver(resize)
        this.resizeObserver.observe(canvas)
      }
      var animate = () => {
        ctx.clearRect(0, 0, canvas.width, canvas.height)
        this.frame++
        if (this.frame % 8 === 0) {
          var gx = this.globalPos.x
          var gy = this.globalPos.y
          var mx = this.myPos.x
          var my = this.myPos.y
          if (gx > 0 && gy > 0) {
            this.particles.push(this.spawnParticle(gx, gy, '#FF4500'))
          }
          if (mx > 0 && my > 0) {
            this.particles.push(this.spawnParticle(mx, my, '#FFD700'))
          }
        }
        if (this.particles.length > 45) {
          this.particles = this.particles.slice(-40)
        }
        this.particles = this.particles.filter((p) => {
          p.life++
          if (p.life > p.maxLife) return false
          var dx = p.targetX - p.x
          var dy = p.targetY - p.y
          var dist = Math.sqrt(dx * dx + dy * dy) || 1
          var force = 0.03 + (p.life / p.maxLife) * 0.06
          p.vx += (dx / dist) * force
          p.vy += (dy / dist) * force
          p.vx *= 0.95
          p.vy *= 0.95
          p.x += p.vx
          p.y += p.vy
          var progress = p.life / p.maxLife
          var alpha = p.opacity * (1 - Math.pow(progress, 2))
          ctx.save()
          ctx.globalAlpha = alpha
          ctx.beginPath()
          ctx.arc(p.x, p.y, p.size * (1 - progress * 0.5), 0, Math.PI * 2)
          ctx.fillStyle = p.color
          ctx.shadowColor = p.color
          ctx.shadowBlur = 8
          ctx.fill()
          ctx.restore()
          return true
        })
        this.animId = requestAnimationFrame(animate)
      }
      animate()
      var cleanup = () => {
        cancelAnimationFrame(this.animId)
        if (this.resizeObserver) this.resizeObserver.disconnect()
      }
      return cleanup
    },
  },
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Orbitron:wght@400;600;700;800;900&family=Space+Grotesk:wght@300;400;500;600;700&display=swap');

.hash-power-orb-wrapper {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1.5rem;
  min-height: 300px;
  background: #050000;
  overflow: hidden;
  padding-top: 0.5rem;
}

.orb-canvas {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 1;
}

.orb-title {
  position: relative;
  z-index: 10;
  text-align: center;
}
.orb-title-text {
  font-family: 'Orbitron', sans-serif;
  font-weight: 600;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.4);
  letter-spacing: 3px;
  text-transform: uppercase;
  margin: 0;
}

.orbs-row {
  position: relative;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 50px;
}

.orb-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
}

.glass-orb {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: default;
  user-select: none;
}

.orb-large {
  width: 130px;
  height: 130px;
  border-radius: 60% 40% 70% 30% / 50% 60% 40% 50%;
}

.orb-medium {
  width: 105px;
  height: 105px;
  border-radius: 40% 60% 30% 70% / 60% 40% 50% 50%;
}

.orb-highlight {
  position: absolute;
  width: 40%;
  height: 30%;
  top: 15%;
  left: 20%;
  border-radius: 50%;
  background: radial-gradient(ellipse, rgba(255, 255, 255, 0.15) 0%, transparent 70%);
  filter: blur(4px);
  pointer-events: none;
}

.orbit-ring {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  border: 1px solid transparent;
  transform: scale(1.15);
}

.orbit-dot {
  position: absolute;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  top: -4px;
  left: 50%;
  transform: translateX(-50%);
}

.orb-content {
  position: relative;
  z-index: 10;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  text-align: center;
  padding: 0 12px;
}

.orb-label {
  font-family: 'Space Grotesk', sans-serif;
  font-size: 11px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.6);
  letter-spacing: 1.5px;
  text-transform: uppercase;
}

.orb-value {
  font-family: 'Orbitron', sans-serif;
  font-weight: 800;
  font-size: 22px;
  line-height: 1;
}

.orb-medium .orb-value {
  font-size: 18px;
}

.orb-unit {
  font-family: 'Space Grotesk', sans-serif;
  font-size: 11px;
  opacity: 0.7;
}


.orb-glow {
  position: absolute;
  inset: 0;
  border-radius: 60% 40% 70% 30% / 50% 60% 40% 50%;
  pointer-events: none;
}

.stats-row {
  position: relative;
  z-index: 10;
  display: flex;
  gap: 0.5rem;
  width: calc(100% - 30px);
}

.stat-card {
  flex: 1;
  border-radius: 12px;
  padding: 12px;
  text-align: center;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.06);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
}

.stat-value {
  font-family: 'Orbitron', sans-serif;
  font-weight: 700;
  font-size: 16px;
  line-height: 1;
  margin-bottom: 4px;
}

.stat-label {
  font-family: 'Space Grotesk', sans-serif;
  font-size: 10px;
  color: rgba(255, 255, 255, 0.4);
  letter-spacing: 0.5px;
}


@keyframes float {
  0%, 100% { transform: translateY(0px) rotate(0deg); }
  33%       { transform: translateY(-12px) rotate(2deg); }
  66%       { transform: translateY(-6px) rotate(-1deg); }
}

@keyframes float-alt {
  0%, 100% { transform: translateY(0px) rotate(0deg); }
  50%       { transform: translateY(-16px) rotate(-3deg); }
}

@keyframes orbit-spin {
  from { transform: scale(1.15) rotate(0deg); }
  to   { transform: scale(1.15) rotate(360deg); }
}

@keyframes breathe-orange {
  0%, 100% {
    box-shadow: 0 0 20px rgba(255, 69, 0, 0.3), 0 0 40px rgba(255, 69, 0, 0.1);
  }
  50% {
    box-shadow: 0 0 40px rgba(255, 69, 0, 0.6), 0 0 80px rgba(255, 69, 0, 0.3), 0 0 120px rgba(255, 69, 0, 0.1);
  }
}

@keyframes breathe-gold-anim {
  0%, 100% {
    box-shadow: 0 0 20px rgba(201, 168, 76, 0.3), 0 0 40px rgba(201, 168, 76, 0.1);
  }
  50% {
    box-shadow: 0 0 40px rgba(201, 168, 76, 0.6), 0 0 80px rgba(201, 168, 76, 0.3);
  }
}

.orb-float     { animation: float 4s ease-in-out infinite; }
.orb-float-alt { animation: float-alt 5s ease-in-out infinite; }
.orbit-spin    { animation: orbit-spin 12s linear infinite; }
.orb-breathe      { animation: breathe-orange 3s ease-in-out infinite; }
.orb-breathe-gold { animation: breathe-gold-anim 3s ease-in-out infinite; }
</style>
